import { useCallback, useEffect, useRef, useState } from "react";

import { directories, uploadToS3 } from "../../../../../utils/S3";
import { getPaymentProofName } from "../../../../PublishingRequests/publishingRequestsUtils";

export const UPLOAD_STATUS = {
  idle: "idle",
  uploading: "uploading",
  uploaded: "uploaded",
};

const IDLE_STATE = {
  status: UPLOAD_STATUS.idle,
  file: null,
  /** Permanent link of the uploaded file: the `payment_proof_url` of the request. */
  url: null,
  progress: 0,
  uploadId: 0,
};

/**
 * A proof saved with the draft: shown as uploaded, named after its link. It has no local
 * copy, so `file.url` is what the preview opens.
 */
const savedProofState = (url) => ({
  status: UPLOAD_STATUS.uploaded,
  file: { name: getPaymentProofName(url), url },
  url,
  progress: 100,
  uploadId: 0,
});

/**
 * Holds the single transfer evidence file, its upload progress and its stored link.
 * `onError(error)` is called when the upload fails; the file is then dropped.
 * `savedUrl` is the proof saved with the draft, if any (PROLINE-4165).
 */
const useEvidenceUpload = ({ onError, savedUrl } = {}) => {
  const [state, setState] = useState(IDLE_STATE);
  /** Only the latest upload may update the state; an older or cancelled one is ignored. */
  const activeUploadRef = useRef(0);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // The event loads after the step mounts: its saved proof shows while no file is picked.
  useEffect(() => {
    if (!savedUrl) return;
    setState((current) =>
      current.status === UPLOAD_STATUS.idle ? savedProofState(savedUrl) : current,
    );
  }, [savedUrl]);

  useEffect(
    () => () => {
      activeUploadRef.current = -1;
    },
    [],
  );

  /** Starting a new upload replaces the previous file. */
  const start = useCallback((file) => {
    const uploadId = activeUploadRef.current + 1;
    activeUploadRef.current = uploadId;
    const isCurrent = () => activeUploadRef.current === uploadId;

    // A new `uploadId` per upload lets the progress bar restart from 0 instead of animating back.
    setState({
      status: UPLOAD_STATUS.uploading,
      file,
      url: null,
      progress: 0,
      uploadId,
    });

    uploadToS3(file, file.name, directories.events, {
      onProgress: (progress) => {
        // 100% is only shown once the stored link is back.
        if (isCurrent()) {
          setState((current) => ({ ...current, progress: Math.min(progress, 99) }));
        }
      },
    })
      .then((data) => {
        if (!isCurrent()) return;
        if (!data?.link) throw new Error("upload link missing");
        setState((current) => ({
          ...current,
          url: data.link,
          progress: 100,
          status: UPLOAD_STATUS.uploaded,
        }));
      })
      .catch((error) => {
        if (!isCurrent()) return;
        setState((current) => ({ ...IDLE_STATE, uploadId: current.uploadId }));
        onErrorRef.current?.(error);
      });
  }, []);

  const clear = useCallback(() => {
    activeUploadRef.current += 1;
    setState((current) => ({ ...IDLE_STATE, uploadId: current.uploadId }));
  }, []);

  return { ...state, start, clear };
};

export default useEvidenceUpload;
